import { Button } from "$components/button/button";
import { ValidatingSubmitButton } from "$components/rhf-fields";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { LocationSelect } from "$routes/inventory/_lib/location-select";
import { gql, useMutation } from "@apollo/client";
import { Box, styled, Typography } from "@material-ui/core";
import { Controller, FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useMoveDrawerContext, useMoveFormContext } from "../inventory-context";
import { useResetTableCache } from "../queries";
import { FormErrorAlert } from "./form-error-alert";
import { MoveLotCard } from "./move-lot-card";
import type { MoveFormValues } from "./use-move-form";

const MOVE_LOTS = gql`
  mutation moveLots($input: MoveLotsInput!) {
    moveLots(input: $input) {
      moves {
        updatedSourceLot {
          expiryDate
          facilityId
          locationId
          id
          quantity
          skuId
        }
        updatedDestinationLot {
          expiryDate
          facilityId
          locationId
          id
          quantity
          skuId
        }
        quantityRequested
        quantityDecremented
        newDestinationLotCreated
      }
    }
  }
`;

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export function MoveForm() {
  const { form, sourceLotFields, removeLot, clearLots } = useMoveFormContext();
  const { closeMenu } = useMoveDrawerContext();
  const { formatMessage } = useIntl();
  const { facilityId } = useUser();

  const resetTableCache = useResetTableCache();

  // Cancelling discards the in-progress move: clear the selected lots (which
  // also unticks their table checkboxes) and close the drawer.
  function handleCancel() {
    form.reset();
    clearLots();
    closeMenu();
  }

  const { errors } = form.formState;

  const [moveLots, { error: moveLotError }] = useMutation(MOVE_LOTS, {
    onCompleted: async () => {
      toast.success(
        formatMessage({
          id: "inventory.move.submitSuccessMessage",
          defaultMessage: "Successfully moved lots",
        }),
      );
      form.reset();
      await resetTableCache({
        facilityId,
        clearLotsForAllSkus: true, // Clear all child lots as a move might hit many skus (and we don't nicely track which skus have been hit)
      });
    },
  });

  async function handleSubmit(formValues: MoveFormValues) {
    if (!formValues.moves) {
      toast.error(
        formatMessage({
          id: "inventory.move.error.noMovesSpecified",
          defaultMessage: "Please specify at least one move",
        }),
      );
      return;
    }

    await moveLots({
      variables: {
        input: {
          facilityId,
          destinationLocationId: formValues.destinationLocationId,
          moves: formValues.moves.map(({ sourceLotId, quantityToMove }) => ({
            sourceLotId,
            quantity: quantityToMove,
          })),
        },
      },
    });
  }

  return (
    <Box marginTop={2}>
      <FormProvider {...form}>
        <Form onSubmit={form.handleSubmit(handleSubmit)}>
          <Controller
            control={form.control}
            name="destinationLocationId"
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <LocationSelect
                {...{ onChange, onBlur, value }}
                error={errors.destinationLocationId?.message}
                id="move-destination-location"
                label={formatMessage({
                  id: "inventory.move.form.destinationLocation",
                  defaultMessage: "Move To",
                })}
              />
            )}
          />
          {sourceLotFields.length === 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              marginY={4}
            >
              <Typography>
                <b>
                  <FormattedMessage
                    id="inventory.move.noLotsSelectedMessage"
                    defaultMessage="Please select medication lots"
                  />
                </b>
              </Typography>
            </Box>
          ) : null}
          {sourceLotFields.map(
            (
              {
                sourceLotId,
                locationName,
                name,
                expiryDate,
                currentLotQuantity,
                externalReferences,
              },
              index,
            ) => (
              <MoveLotCard
                key={sourceLotId}
                lotId={sourceLotId}
                locationName={locationName}
                expiryDate={expiryDate}
                lotName={name}
                currentLotQuantity={currentLotQuantity}
                quantityInputName={`moves.${index}.quantityToMove`}
                locationError={errors?.moves?.[index]?.locationId?.message}
                onRemoveClick={() => removeLot(index)}
                externalReferences={externalReferences}
              />
            ),
          )}
          {moveLotError && <FormErrorAlert marginBottom={2} />}
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              color="secondary"
              type="button"
              onClick={handleCancel}
              data-testid="move-cancel-button"
            >
              <FormattedMessage
                id="inventory.move.form.cancel"
                defaultMessage="Cancel Move"
              />
            </Button>
            <ValidatingSubmitButton
              type="submit"
              data-testid="move-submit-button"
              disabled={form.formState.isSubmitting}
              loading={form.formState.isSubmitting}
            >
              <FormattedMessage
                id="inventory.move.form.sumbit"
                defaultMessage="Move"
              />
            </ValidatingSubmitButton>
          </Box>
        </Form>
      </FormProvider>
    </Box>
  );
}
